import { Button, Col, Form , Input , Row, Select , DatePicker, message, Table, Tooltip} from "antd";
import React, { useEffect, useState } from "react";
import './transaction-form.style.scss';
import { PlusOutlined } from '@ant-design/icons';
import { AgentsSelect } from './../search-components/agents-select/agents-select.component';
import { useDispatch } from "react-redux";
import { createAgentTransaction, fetchPostPaidAgentUnpaidTickets } from "../../actions/agents.actions";
import { isValidNumber, isValid, isValidArrayWithValues } from "../../utils/utilities";
import moment from "moment";
import { POST_PAID_USER } from "../../utils/constant";
import { getReadableDate, getLocalDateAndTime } from '../../utils/date.utils';
import { isRechargeAgent, UpiRechargeModal } from '../upi-booking/upi-recharge.component';

const { Option } = Select;

export const TransactonsFormPage = (props) => {

    let {
        showTransactionForm,
        setShowTransactionForm,
        agents,
        selectedAgentId,
        onCompleteTransaction,
        transactionSummaryData
    } = props;

    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const [postPaidAgentUnpaidTickets, setPostPaidAgentUnpaidTickets] = useState([]);
    const [paymentAmounts, setPaymentAmounts] = useState({});
    const [isPostPaidAgent, setIsPostPaidAgent] = useState(false);
    const [dueAmount, setDueAmount] = useState(0);
    const [receivedAmount, setReceivedAmount] = useState(0);
    const [assignChecked, setAssignChecked] = useState(false);
    const [enteredAmount, setEnteredAmount] = useState('');

    // Prepaid "agent" of a UPI-enabled operator: show UPI recharge instead of Create.
    const rechargeAgent = isRechargeAgent();
    const [showRecharge, setShowRecharge] = useState(false);
    const [rechargeAmount, setRechargeAmount] = useState(0);

    const handleRecharge = () => {
        form.validateFields(['amount']).then((values) => {
            const amount = Number(values.amount);
            if (!amount || amount <= 0) {
                message.error('Please enter a valid recharge amount');
                return;
            }
            setRechargeAmount(amount);
            setShowRecharge(true);
        }).catch(() => {});
    };

    const onRechargeSuccess = () => {
        setShowRecharge(false);
        resetForm();
        if (typeof onCompleteTransaction === 'function') {
            onCompleteTransaction();
        }
    };

    const onClickPlus = ()=>{
        setShowTransactionForm(true);
    }

    const createAgentTransactionDispatch=(values)=>{
        dispatch(createAgentTransaction({...values},()=>{
            resetForm();
            // Check if onCompleteTransaction is a valid function and call it
            if (typeof onCompleteTransaction === 'function') {
                onCompleteTransaction();
            }
            message.success("Transaction created successfully!")
        },()=>{}))
    }

    useEffect(()=>{
        if(isValidNumber(selectedAgentId)){
            setShowTransactionForm(true);
            if(isValid(agents) && agents.length == 1){
                if (agents[0].agent_type === POST_PAID_USER){
                    if(isValid(transactionSummaryData)){
                        setPostPaidAgentUnpaidTickets(transactionSummaryData);
                        setDueAmount(transactionSummaryData.reduce((sum, ticket) => sum + parseFloat(ticket.balanceDue || 0), 0));
                    }
                    else{
                        fetchPostPaidAgentUnpaidTicketsAction(agents[0].id);
                    }
                    setIsPostPaidAgent(true);
                }
            }
        }
    },[selectedAgentId]);

    const resetForm=()=>{
        form.resetFields();
        setIsPostPaidAgent(false);
        setPostPaidAgentUnpaidTickets([]);
        setPaymentAmounts({});
        setDueAmount(0);
        setReceivedAmount(0);
        setAssignChecked(false);
        setEnteredAmount('');
    }

    const fetchPostPaidAgentUnpaidTicketsAction = (agentId)=>{
        fetchPostPaidAgentUnpaidTickets(agentId,(data)=>{
            setPostPaidAgentUnpaidTickets(data);
            // Calculate and set due amount
            const totalDue = data.reduce((sum, ticket) => sum + parseFloat(ticket.balanceDue || 0), 0);
            setDueAmount(totalDue);
        },()=>{})
    }

    const handleFormValuesChange = (changedValues) => {
        if ('amount' in changedValues) {
            setEnteredAmount(changedValues.amount);
            // The checkbox meaning changes with the amount, so drop any prior assignment
            if (assignChecked) {
                setAssignChecked(false);
                setPaymentAmounts({});
                setReceivedAmount(0);
            }
        }
        // Check if agentName field changed
        if (changedValues.agentName && isValidNumber(changedValues.agentName) && agents && agents.length > 0) {
            const selectedAgent = agents.find(agent => agent.id === changedValues.agentName);
            if (selectedAgent && selectedAgent.agent_type === POST_PAID_USER) {
                fetchPostPaidAgentUnpaidTicketsAction(selectedAgent.id);
                setIsPostPaidAgent(true);
            }else{
                setIsPostPaidAgent(false);
            }
        }
    }

    const handleReceiveFullAmountChange = (checked) => {
        setAssignChecked(checked);
        if (checked) {
            // Set all payment amounts to their balance due
            const newPaymentAmounts = {};
            const totalBalanceDue = postPaidAgentUnpaidTickets.reduce((sum, ticket) => sum + parseFloat(ticket.balanceDue || 0), 0);

            postPaidAgentUnpaidTickets.forEach(ticket => {
                newPaymentAmounts[ticket.pnr] = parseFloat(ticket.balanceDue || 0).toFixed(2);
            });
            setPaymentAmounts(newPaymentAmounts);
            setReceivedAmount(totalBalanceDue);

            // Set the amount field with total balance due
            form.setFieldsValue({ amount: totalBalanceDue });
            setEnteredAmount(totalBalanceDue);
        } else {
            // Clear all payment amounts
            setPaymentAmounts({});
            setReceivedAmount(0);
            // Clear the amount field
            form.setFieldsValue({ amount: '' });
            setEnteredAmount('');
        }
    }

    const handleAutoAssignChange = (checked) => {
        setAssignChecked(checked);
        if (!checked) {
            setPaymentAmounts({});
            setReceivedAmount(0);
            return;
        }
        // Fill tickets by ascending DOJ: oldest journeys get paid in full first,
        // the next one gets whatever remains as a partial payment.
        let remaining = parseFloat(form.getFieldValue('amount')) || 0;
        const sortedTickets = [...postPaidAgentUnpaidTickets].sort(
            (a, b) => moment(a.start_date).unix() - moment(b.start_date).unix()
        );
        const newPaymentAmounts = {};
        let totalAssigned = 0;
        sortedTickets.forEach(ticket => {
            const balanceDue = parseFloat(ticket.balanceDue || 0);
            if (remaining <= 0 || balanceDue <= 0) {
                return;
            }
            const payAmount = Math.round(Math.min(balanceDue, remaining) * 100) / 100;
            newPaymentAmounts[ticket.pnr] = payAmount.toFixed(2);
            remaining = Math.round((remaining - payAmount) * 100) / 100;
            totalAssigned += payAmount;
        });
        setPaymentAmounts(newPaymentAmounts);
        setReceivedAmount(totalAssigned);
    }

    // While the checkbox is checked, ticket edits drive the Amount field so the
    // two stay in sync (and the checkbox label follows from the new amount).
    const syncAmountFieldToPayments = (totalReceived) => {
        if (assignChecked) {
            const roundedTotal = Math.round(totalReceived * 100) / 100;
            form.setFieldsValue({ amount: roundedTotal });
            setEnteredAmount(roundedTotal);
        }
    }

    const handlePayInFull = (pnr, balanceDue) => {
        const ticket = postPaidAgentUnpaidTickets.find(t => t.pnr === pnr);
        const actualBalanceDue = parseFloat(ticket?.balanceDue || 0);

        const newPaymentAmounts = {
            ...paymentAmounts,
            [pnr]: actualBalanceDue.toFixed(2)
        };
        setPaymentAmounts(newPaymentAmounts);

        // Calculate and set received amount
        const totalReceived = Object.values(newPaymentAmounts).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
        setReceivedAmount(totalReceived);
        syncAmountFieldToPayments(totalReceived);
    }

    const handlePaymentAmountChange = (pnr, value) => {
        // Allow only numbers with at most 2 decimal digits
        if (value !== '' && !/^\d*(\.\d{0,2})?$/.test(value)) {
            return;
        }
        const numericValue = parseFloat(value);
        const ticket = postPaidAgentUnpaidTickets.find(t => t.pnr === pnr);
        const balanceDue = parseFloat(ticket?.balanceDue || 0);
        
        // Validation: value should be greater than 0 and less than or equal to balance due
        if (value === '' || (numericValue > 0 && numericValue <= balanceDue)) {
            const newPaymentAmounts = {
                ...paymentAmounts,
                [pnr]: value
            };
            setPaymentAmounts(newPaymentAmounts);
            
            // Calculate and set received amount
            const totalReceived = Object.values(newPaymentAmounts).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
            setReceivedAmount(totalReceived);
            syncAmountFieldToPayments(totalReceived);
        } else {
            // Show error message for invalid amount
            if (numericValue > balanceDue) {
                message.error(`Payment amount cannot exceed balance due (₹${balanceDue})`);
            } else if (numericValue <= 0) {
                message.error("Payment amount must be greater than 0");
            }
        }
    }

    const onFinish = (values) =>{
        values.agentId = values.agentName;
        values.status = "Completed";
        // Check if selected agent is postpaid
        if (isPostPaidAgent) {
            // Validation 1: At least 1 Payment field should be entered with valid number
            const paymentValues = Object.values(paymentAmounts);
            const validPayments = paymentValues.filter(amount => parseFloat(amount) > 0);
            
            if (validPayments.length === 0) {
                message.error("At least 1 payment field must be entered with a valid amount");
                return;
            }
            
            // Validation 2: Amount entered should match with Total amount of payment
            const totalPaymentAmount = Object.values(paymentAmounts).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
            const formAmount = parseFloat(values.amount);
            
            if (totalPaymentAmount !== formAmount) {
                message.error(`Transaction amount (₹${formAmount}) must match total payment amount (₹${totalPaymentAmount})`);
                return;
            }
            
            // Validation 3: Add payment data to the values
            values.payments = {};
            Object.keys(paymentAmounts).forEach(pnr => {
                const paymentAmount = parseFloat(paymentAmounts[pnr]);
                if (paymentAmount > 0) {
                    // Find the ticket_id for this PNR
                    const ticket = postPaidAgentUnpaidTickets.find(t => t.pnr === pnr);
                    if (ticket && ticket.ticket_id) {
                        const balanceDue = parseFloat(ticket.balanceDue);
                        const status = paymentAmount < balanceDue ? "Partial Paid" : "Paid";
                        
                        values.payments[ticket.ticket_id] = {
                            paymentAmount: paymentAmount,
                            status: status
                        };
                    }
                }
            });
        }
        createAgentTransactionDispatch(values);
    }

    const renderPnrWithTooltip = (pnr, record) => {
        let tripDetails = record.tripInfo;
        let isCancelledSeats = isValidArrayWithValues(tripDetails.cancelledSeatNumber)
        let isConfirmedSeats = isValidArrayWithValues(tripDetails.confirmedSeatNumber)
        const tooltipContent = (
            <div>
                <div><strong>DOJ:</strong> {getReadableDate(tripDetails.startDate)}</div>
                <div><strong>Trip Details:</strong> {tripDetails.tripDetails || 'N/A'}</div>
                <div><strong>Pass Details:</strong> {tripDetails.passengerDetails || 'N/A'}</div>
                {isConfirmedSeats&&<div><strong>Confirmed Seat #:</strong> {tripDetails.confirmedSeatNumber.join(', ')}</div>}
                {isCancelledSeats&&<div><strong>Cancelled Seat #:</strong> {tripDetails.cancelledSeatNumber.join(', ')}</div>}
                {isValid(tripDetails.origin) && <div><strong>Origin:</strong> {tripDetails.origin}</div>}
                {isValid(tripDetails.destination) && <div><strong>Destination:</strong> {tripDetails.destination}</div>}
                <div><strong>Booking Time:</strong> {getLocalDateAndTime(tripDetails.bookingDate)}</div>
                {isConfirmedSeats && <>
                        <div><strong>{isCancelledSeats&&'Confirm Tkt '}Fare:</strong> {tripDetails.confirmedNetAmount.toLocaleString()}</div>
                        <div><strong>{isCancelledSeats&&'Confirm Tkt '}GST:</strong> {tripDetails.ticketGST.toLocaleString()}</div>
                        {isValid(tripDetails.ticketCommission)&&<div><strong>{isCancelledSeats&&'Confirm Tkt '}Commission:</strong> {tripDetails.ticketCommission.toLocaleString()}</div>}
                        <div><strong>{isCancelledSeats&&'Confirm Tkt '}Net Amount:</strong> {tripDetails.confirmedNetAmount.toLocaleString()}</div>
                    </>
                }
                {isCancelledSeats && <>
                    <div><strong>Cancel Penalty:</strong> {tripDetails.ticketPenalty.toLocaleString()}</div>
                    <div><strong>Penalty Commission:</strong> {tripDetails.ticketPenaltyCommission.toLocaleString()}</div>
                    <div><strong>{isConfirmedSeats&&'Cancel Tkt '}Net Amount:</strong> {tripDetails.cancelledNetAmount.toLocaleString()}</div>
                </>}
                <br/>
                <div><strong>Net Amount:</strong> {record.netAmount.toLocaleString()}</div>
                <div><strong>Amount Received:</strong> {record.netAmount - record.balanceDue}</div>
                <div><strong>Balance Due:</strong> {record.balanceDue.toLocaleString()}</div>
            </div>
        );

        return (
            <Tooltip title={tooltipContent} placement="top">
                <a style={{ color: 'blue', textDecoration: 'underline' }}>
                    {pnr}
                </a>
            </Tooltip>
        );
    }

    if(showTransactionForm){
        return (
            <div className="agent-form-root">
                <div className="fontWeightBold new-transactions-text">
                    New Transaction 
                </div>
                <div className="agent-form-inside">
                    <Form
                        form={form}
                        initialValues={{ date : moment().utcOffset("+05:30"), agentName: rechargeAgent ? global.agentId : selectedAgentId}}
                        className = "agent-form"
                        onFinish={onFinish}
                        onValuesChange={handleFormValuesChange}
                    >
                        {!rechargeAgent &&
                        <AgentsSelect
                            agents={agents}
                            showLabel={true}
                            isManditory={true}
                            showHeadOffice={true}
                            showDriver={false}
                            selectedAgentId={selectedAgentId}
                        />}
                        <Row gutter={[16.16]}>
                            <Col span={12}>
                                <Form.Item
                                label="Date"
                                rules={[{ required: true, message: 'Please input date' }]}
                                name="date"
                                >
                                        <DatePicker  />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                label="Amount (₹)"
                                rules={[{ required: true, message: 'Please input amount' }]}
                                name="amount"
                                >
                                    <Input placeholder="Amount"  />
                                </Form.Item>
                            </Col>
                        </Row>
                        
                        {/* Full Amount / Auto Assign Checkbox for Postpaid Agents */}
                        {isPostPaidAgent && postPaidAgentUnpaidTickets && postPaidAgentUnpaidTickets.length > 0 && (() => {
                            const parsedAmount = parseFloat(enteredAmount);
                            const isAutoAssignMode = !isNaN(parsedAmount) && parsedAmount >= 1 && parsedAmount !== dueAmount;
                            return (
                                <Row gutter={[16.16]}>
                                    <Col offset={12}>
                                    <div style={{ marginBottom: '15px' }}>
                                        <input
                                            type="checkbox"
                                            id="receiveFullAmount"
                                            checked={assignChecked}
                                            onChange={(e) => {
                                                if (isAutoAssignMode) {
                                                    handleAutoAssignChange(e.target.checked);
                                                } else {
                                                    handleReceiveFullAmountChange(e.target.checked);
                                                }
                                            }}
                                        />
                                        <label htmlFor="receiveFullAmount" style={{ marginLeft: '8px' }}>
                                            {isAutoAssignMode
                                                ? <>Auto assign ₹{parsedAmount.toLocaleString()} to tickets</>
                                                : <>Receive full amount ₹{dueAmount.toLocaleString()}</>
                                            }
                                        </label>
                                    </div>
                                    </Col>
                                </Row>
                            );
                        })()}
                        
                        <Form.Item
                                label="Description"
                                rules={[{ required: true, message: 'Please input description' }]}
                                name="description"
                                >
                                <Input placeholder="Description"  />
                        </Form.Item>
                        <Row  gutter={[16.16]} style={rechargeAgent ? {display:'none'} : {}}>
                            <Col>
                                <Form.Item
                                    label="Payment Type"
                                    rules={[{ required: !rechargeAgent, message: 'Please select payment type' }]}
                                    name="paymentType"
                                    >
                                         <Select
                                            size="large"
                                            showSearch
                                            placeholder="Select a option"
                                            optionFilterProp="childrens"
                                            filterOption={(input, option) =>
                                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                            }
                                            bordered={true}
                                            className="defalut-select-style"
                                        >
                                            <Option value={'Cash'}>Cash</Option>
                                            <Option value={'Online'}>Online</Option>
                                        </Select>
                                </Form.Item>
                            </Col>
                            <Col>
                            <Form.Item
                                label="Ref Id"
                                rules={[{ required: false, message: 'Please input amount' }]}
                                name="refId"
                                >
                                    <Input placeholder="Ref Id"  />
                        </Form.Item>
                            </Col>
                        </Row>
                        
                        {/* Unpaid Tickets Table */}
                        {isPostPaidAgent && postPaidAgentUnpaidTickets && postPaidAgentUnpaidTickets.length > 0 && (
                            <div style={{ marginTop: '20px' }}>
                                <div style={{ 
                                    backgroundColor: '#f5f5f5', 
                                    padding: '10px', 
                                    fontWeight: 'bold',
                                    marginBottom: '10px'
                                }}>
                                    Unpaid Tickets
                                </div>
                                
                                <Table
                                    dataSource={postPaidAgentUnpaidTickets}
                                    pagination={false}
                                    size="small"
                                    bordered
                                    columns={[
                                        {
                                            title: 'DOJ & PNR',
                                            dataIndex: 'start_date',
                                            key: 'start_date',
                                            width: 100,
                                            sorter: (a, b) => moment(a.start_date).unix() - moment(b.start_date).unix(),
                                            render: (text, record) => {
                                                return (
                                                    <div>
                                                        <Row gutter={[16.16]}>
                                                            <Col>
                                                                <div>{moment(text).format('DD/MM/YYYY')}</div>
                                                            </Col>
                                                        </Row>
                                                        <Row gutter={[16.16]}>
                                                            <Col>
                                                                <div>{renderPnrWithTooltip(record.pnr, record)}</div>
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                )
                                            }
                                        },
                                        {
                                            title: 'Passenger Details',
                                            dataIndex: 'passengerDetails',
                                            key: 'passengerDetails',
                                            width: 120,
                                            render: (text, record) => {
                                                let passengerDetails = record.tripInfo.passengerDetails;
                                                let passName = passengerDetails.split('/')[0];
                                                let passPhone = passengerDetails.split('/')[1];
                                                return (
                                                    <div>
                                                        <div>{passName}</div>
                                                        <div>{passPhone}</div>
                                                    </div>
                                                )
                                            }
                                        },
                                        {
                                            title: 'Seats',
                                            dataIndex: 'seats',
                                            key: 'seats',
                                            width: 80,
                                            render: (text, record) => {
                                                let cancelledSeatNumber = record.tripInfo.cancelledSeatNumber;
                                                let confirmedSeatNumber = record.tripInfo.confirmedSeatNumber;
                                                return (
                                                    <div>
                                                        {cancelledSeatNumber.length > 0 && <div style={{ color: 'red' }}>{cancelledSeatNumber.join(', ')}</div>}
                                                        {confirmedSeatNumber.length > 0 && <div style={{ color: 'green' }}>{confirmedSeatNumber.join(', ')}</div>}
                                                    </div>
                                                )
                                            }
                                        },
                                        {
                                            title: 'Trip Details',
                                            dataIndex: 'tripDetails',
                                            key: 'tripDetails',
                                            width: 200,
                                            render: (text, record) => {
                                                let tripDetails = record.tripInfo.tripDetails;
                                                let origin = record.tripInfo.origin;
                                                let destination = record.tripInfo.destination;
                                                let showOrigDest = true;
                                                if(tripDetails.includes(origin) && tripDetails.includes(destination)){
                                                    showOrigDest = false;
                                                }
                                                return (
                                                    <div>
                                                        <div>{`${tripDetails}`}</div>
                                                        {showOrigDest && <div>{`(${origin} > ${destination})`}</div>}
                                                    </div>
                                                )
                                            }
                                        },
                                        {
                                            title: 'Net Amount',
                                            dataIndex: 'netAmount',
                                            key: 'netAmount',
                                            width: 120,
                                            render: (text) => `₹${(parseFloat(text) || 0).toFixed(2)}`
                                        },
                                        {
                                            title: 'Balance Due',
                                            dataIndex: 'balanceDue',
                                            key: 'balanceDue',
                                            width: 120,
                                            render: (text) => `₹${(parseFloat(text) || 0).toFixed(2)}`
                                        },
                                        {
                                            title: 'Payment',
                                            key: 'payment',
                                            width: 200,
                                            render: (_, record) => (
                                                <div>
                                                    <Input 
                                                        placeholder="Enter amount" 
                                                        style={{ marginBottom: '5px' }}
                                                        value={paymentAmounts[record.pnr] || ''}
                                                        onChange={(e) => handlePaymentAmountChange(record.pnr, e.target.value)}
                                                    />
                                                    <div>
                                                        <a 
                                                            href="#" 
                                                            style={{ color: 'blue', textDecoration: 'underline' }}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handlePayInFull(record.pnr, record.balanceDue || record.netAmount);
                                                            }}
                                                        >
                                                            Pay in full
                                                        </a>
                                                    </div>
                                                </div>
                                            )
                                        }
                                    ]}
                                />
                                
                                {/* Total Summary */}
                                <div style={{ 
                                    textAlign: 'right', 
                                    marginTop: '10px',
                                    fontWeight: 'bold'
                                }}>
                                    Total: ₹{Object.values(paymentAmounts).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0).toLocaleString()}
                                </div>
                                
                                {/* Payment Details */}
                                <div style={{ 
                                    backgroundColor: '#f5f5f5', 
                                    padding: '15px', 
                                    marginTop: '15px',
                                    borderRadius: '4px'
                                }}>
                                    <Row gutter={[16, 8]}>
                                        <Col span={8}>
                                            <div>Due Amount: ₹{dueAmount.toLocaleString()}</div>
                                        </Col>
                                    </Row>
                                    <Row gutter={[16, 8]}>
                                        <Col span={8}>
                                            <div>Received Amount: ₹{receivedAmount.toLocaleString()}</div>
                                        </Col>
                                    </Row>
                                    <Row gutter={[16, 8]}>
                                        <Col span={8}>
                                            <div>Balance Remaining: ₹{(dueAmount - receivedAmount).toLocaleString()}</div>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        )}
                        
                        <Row gutter={[16,16]}>
                            <Col>
                                <Button
                                    className="cancel-button"
                                    onClick={() => {
                                        resetForm();
                                        if (typeof onCompleteTransaction === 'function') {
                                            onCompleteTransaction();
                                        }
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Col>
                            <Col>
                                {rechargeAgent
                                    ? <Button type="primary" onClick={handleRecharge}>Recharge</Button>
                                    : <Button htmlType="submit">Create</Button>
                                }
                            </Col>
                        </Row>
                    </Form>
                    <UpiRechargeModal
                        visible={showRecharge}
                        amount={rechargeAmount}
                        onClose={() => setShowRecharge(false)}
                        onSuccess={onRechargeSuccess}
                    />
                </div>
            </div>
        );
    }else{
        return (
            <div className="new-transactions-button">
                <div className="fontWeightBold new-transactions-text">
                    New Transaction 
                </div>
                <div>
                    <Button
                        className="plus-button"
                        onClick={onClickPlus}
                        icon={<PlusOutlined />}
                    />
                </div>
            </div>
        );
    }
}